import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  CreditCard,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Loader2,
  AlertTriangle,
  CheckCircle,
  Banknote,
  Plus,
  MoreVertical,
  Trash2,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useCreditCardInvoice } from '@/hooks/use-credit-card-invoice';
import { toast } from '@/hooks/use-toast';
import * as LucideIcons from 'lucide-react';
import { TransactionModal } from '@/components/transactions/TransactionModal';

interface CreditCardInvoiceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  creditCardId: string | null;
  onSuccess?: () => void;
}

export function CreditCardInvoiceModal({
  open,
  onOpenChange,
  creditCardId,
  onSuccess
}: CreditCardInvoiceModalProps) {
  const {
    creditCard,
    transactions,
    loading,
    invoicePeriods,
    currentPeriod,
    selectedPeriodIndex,
    setSelectedPeriodIndex,
    invoiceSummary,
    isOverdue,
    isCurrentInvoice,
    refetch
  } = useCreditCardInvoice(creditCardId);

  const [checkingAccounts, setCheckingAccounts] = useState<any[]>([]);
  const [selectedCheckingAccountId, setSelectedCheckingAccountId] = useState<string>('');
  const [paymentDate, setPaymentDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
  const [showPaymentConfirm, setShowPaymentConfirm] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [showAddTransaction, setShowAddTransaction] = useState(false);
  const [deleteTransaction, setDeleteTransaction] = useState<any | null>(null);

  // Fetch checking accounts for payment
  useEffect(() => {
    if (!open) return;

    const fetchCheckingAccounts = async () => {
      const { data } = await supabase
        .from('accounts')
        .select('id, name, balance, institution, color')
        .eq('is_active', true)
        .in('type', ['checking', 'savings'])
        .order('name');
      
      setCheckingAccounts(data || []);
      if (data && data.length > 0) {
        setSelectedCheckingAccountId(data[0].id);
      }
    };

    fetchCheckingAccounts();
  }, [open]);

  const handleDeleteTransaction = async () => {
    if (!deleteTransaction) return;

    try {
      const { error } = await supabase
        .from('transactions')
        .delete()
        .eq('id', deleteTransaction.id);

      if (error) throw error;

      toast({
        title: 'Lançamento excluído',
        description: `"${deleteTransaction.description}" foi removido da fatura.`,
      });

      setDeleteTransaction(null);
      refetch();
      onSuccess?.();
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Erro ao excluir lançamento',
        description: error.message,
      });
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDate = (dateStr: string) => {
    return format(new Date(dateStr), 'dd MMM', { locale: ptBR });
  };

  const handlePayInvoice = async () => {
    if (!creditCardId || !selectedCheckingAccountId || !currentPeriod) return;

    setProcessing(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      const paymentAmount = invoiceSummary.invoiceTotal;
      const paymentDescription = `Pagamento Fatura ${creditCard?.name} - ${currentPeriod.label}`;

      // 1. Create expense transaction in checking account (debit from bank account)
      const { error: expenseError } = await supabase
        .from('transactions')
        .insert({
          user_id: user.id,
          account_id: selectedCheckingAccountId,
          description: paymentDescription,
          amount: paymentAmount,
          type: 'expense',
          transaction_date: paymentDate,
          is_paid: true,
          is_fixed: false,
          is_recurring: false
        });

      if (expenseError) throw expenseError;

      // 2. Mark all unpaid transactions in this invoice period as paid
      // and create a payment record (income) in the credit card to track the payment
      const invoiceDate = format(currentPeriod.dueDate, 'yyyy-MM-01');
      const { error: paymentError } = await supabase
        .from('transactions')
        .insert({
          user_id: user.id,
          account_id: creditCardId,
          description: `Pagamento Fatura - ${currentPeriod.label}`,
          amount: paymentAmount,
          type: 'income',
          transaction_date: paymentDate,
          is_paid: true,
          is_fixed: false,
          is_recurring: false,
          notes: `Pago via ${selectedCheckingAccount?.name}`,
          credit_card_invoice_date: invoiceDate
        });

      if (paymentError) throw paymentError;

      toast({
        title: 'Fatura paga com sucesso!',
        description: `${formatCurrency(paymentAmount)} debitado da conta selecionada.`,
      });

      setShowPaymentConfirm(false);
      onOpenChange(false);
      onSuccess?.();
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Erro ao pagar fatura',
        description: error.message,
      });
    } finally {
      setProcessing(false);
    }
  };

  const getCategoryIcon = (iconName: string) => {
    const Icon = (LucideIcons as any)[iconName] || LucideIcons.CircleDot;
    return Icon;
  };

  const selectedCheckingAccount = checkingAccounts.find(a => a.id === selectedCheckingAccountId);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Fatura {creditCard?.name}
            </DialogTitle>
          </DialogHeader>

          {/* Period Navigation */}
          <div className="flex items-center justify-between gap-2 py-2">
            <Button
              variant="ghost"
              size="icon"
              disabled={selectedPeriodIndex >= invoicePeriods.length - 1}
              onClick={() => setSelectedPeriodIndex(prev => prev + 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            
            <div className="text-center flex-1">
              <p className="font-medium capitalize">{currentPeriod?.label}</p>
              {currentPeriod && (
                <p className="text-xs text-muted-foreground">
                  {format(currentPeriod.startDate, 'dd/MM')} a {format(currentPeriod.endDate, 'dd/MM')}
                  {' • '}Vence {format(currentPeriod.dueDate, 'dd/MM/yyyy')}
                </p>
              )}
            </div>

            <Button
              variant="ghost"
              size="icon"
              disabled={selectedPeriodIndex <= 0}
              onClick={() => setSelectedPeriodIndex(prev => prev - 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          {/* Invoice Summary */}
          <Card className={`border-2 ${isOverdue ? 'border-destructive bg-destructive/5' : 'border-primary/20'}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total da Fatura</p>
                  <p className={`text-3xl font-bold ${invoiceSummary.invoiceTotal > 0 ? 'text-destructive' : 'text-success'}`}>
                    {formatCurrency(invoiceSummary.invoiceTotal)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {invoiceSummary.transactionCount} transações
                  </p>
                </div>
                <div className="text-right space-y-1">
                  {isOverdue && (
                    <Badge variant="destructive" className="gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      Vencida
                    </Badge>
                  )}
                  {isCurrentInvoice && !isOverdue && (
                    <Badge variant="secondary" className="gap-1">
                      <Calendar className="h-3 w-3" />
                      Fatura Atual
                    </Badge>
                  )}
                  {invoiceSummary.invoiceTotal <= 0 && (
                    <Badge variant="outline" className="gap-1 text-green-600 border-green-600">
                      <CheckCircle className="h-3 w-3" />
                      Paga
                    </Badge>
                  )}
                </div>
              </div>

              {/* Payment Section */}
              {invoiceSummary.invoiceTotal > 0 && (
                <div className="mt-4 pt-4 border-t space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Banknote className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm font-medium">Pagar com:</span>
                      </div>
                      <Select
                        value={selectedCheckingAccountId}
                        onValueChange={setSelectedCheckingAccountId}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione a conta" />
                        </SelectTrigger>
                        <SelectContent>
                          {checkingAccounts.map((account) => (
                            <SelectItem key={account.id} value={account.id}>
                              <div className="flex items-center gap-2">
                                <div
                                  className="h-2 w-2 rounded-full"
                                  style={{ backgroundColor: account.color }}
                                />
                                <span>{account.name}</span>
                                <span className="text-muted-foreground">
                                  ({formatCurrency(Number(account.balance))})
                                </span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm font-medium">Data do pagamento:</span>
                      </div>
                      <input
                        type="date"
                        value={paymentDate}
                        onChange={(e) => setPaymentDate(e.target.value)}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      />
                    </div>
                  </div>
                  
                  <Button 
                    className="w-full" 
                    onClick={() => setShowPaymentConfirm(true)}
                    disabled={!selectedCheckingAccountId || checkingAccounts.length === 0}
                  >
                    <CreditCard className="h-4 w-4 mr-2" />
                    Pagar Fatura
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Transactions List */}
          <div className="flex-1 min-h-0">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium">Lançamentos</h4>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowAddTransaction(true)}
              >
                <Plus className="h-4 w-4 mr-1" />
                Adicionar
              </Button>
            </div>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : transactions.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                Nenhuma transação neste período
              </div>
            ) : (
              <ScrollArea className="h-[250px]">
                <div className="space-y-2 pr-4">
                  {transactions.map((transaction) => {
                    const CategoryIcon = transaction.category 
                      ? getCategoryIcon(transaction.category.icon) 
                      : LucideIcons.CircleDot;
                    
                    return (
                      <div
                        key={transaction.id}
                        className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className="h-8 w-8 rounded-full flex items-center justify-center"
                            style={{ backgroundColor: transaction.category?.color || '#6366f1' }}
                          >
                            <CategoryIcon className="h-4 w-4 text-white" />
                          </div>
                          <div>
                            <p className="text-sm font-medium line-clamp-1">
                              {transaction.description}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {formatDate(transaction.transaction_date)}
                              {transaction.category && ` • ${transaction.category.name}`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className={`font-medium ${transaction.type === 'expense' ? 'text-destructive' : 'text-green-600'}`}>
                            {transaction.type === 'expense' ? '-' : '+'}
                            {formatCurrency(Number(transaction.amount))}
                          </span>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0">
                                <MoreVertical className="h-3 w-3" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onClick={() => setDeleteTransaction(transaction)}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Excluir
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Confirmation Dialog */}
      <AlertDialog open={showPaymentConfirm} onOpenChange={setShowPaymentConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Pagamento da Fatura</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>Você está prestes a pagar a fatura do cartão <strong>{creditCard?.name}</strong>.</p>
                <div className="bg-muted p-3 rounded-lg space-y-1">
                  <div className="flex justify-between">
                    <span>Valor da fatura:</span>
                    <span className="font-bold">{formatCurrency(invoiceSummary.invoiceTotal)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Conta de débito:</span>
                    <span className="font-medium">{selectedCheckingAccount?.name}</span>
                  </div>
                  <div className="flex justify-between text-sm text-muted-foreground">
                    <span>Saldo atual:</span>
                    <span>{formatCurrency(Number(selectedCheckingAccount?.balance || 0))}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Saldo após pagamento:</span>
                    <span className={Number(selectedCheckingAccount?.balance || 0) - invoiceSummary.invoiceTotal < 0 ? 'text-destructive' : ''}>
                      {formatCurrency(Number(selectedCheckingAccount?.balance || 0) - invoiceSummary.invoiceTotal)}
                    </span>
                  </div>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={processing}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handlePayInvoice} disabled={processing}>
              {processing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processando...
                </>
              ) : (
                'Confirmar Pagamento'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Transaction Confirmation */}
      <AlertDialog open={!!deleteTransaction} onOpenChange={(open) => !open && setDeleteTransaction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Lançamento</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir <strong>"{deleteTransaction?.description}"</strong> ({deleteTransaction ? (deleteTransaction.type === 'expense' ? '-' : '+') + formatCurrency(Number(deleteTransaction.amount)) : ''})? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTransaction}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add Transaction Modal */}
      <TransactionModal
        open={showAddTransaction}
        onOpenChange={setShowAddTransaction}
        onSuccess={() => {
          refetch();
          onSuccess?.();
        }}
        defaultAccountId={creditCardId || undefined}
      />
    </>
  );
}
